import { scheduleWhileVisible } from "@/lib/schedule-while-visible";

/** How often to poll for a newer service worker while the app stays open. */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * Wires service-worker update *detection* to a registration and returns a
 * cleanup function that tears every listener down.
 *
 * The browser does not poll for a new worker on its own — iOS Safari in
 * particular never does while an installed PWA is resumed from its snapshot.
 * So the app asks explicitly, on the triggers `scheduleWhileVisible` provides:
 * when the document becomes visible (the iOS-critical trigger), on a periodic
 * timer (covers a long-lived foreground session), and when the device regains
 * connectivity (an offline device cannot fetch a new worker). Checks are
 * skipped while the tab is hidden or offline — pointless work.
 *
 * A check that rejects (transient network failure) is swallowed: it is an
 * expected outcome, not an error to report, and the next trigger retries.
 * See [[app-update-banner.spec]].
 */
export function scheduleUpdateChecks(
  registration: ServiceWorkerRegistration,
  intervalMs: number = UPDATE_CHECK_INTERVAL_MS,
): () => void {
  return scheduleWhileVisible(() => {
    void registration.update().catch(() => {});
  }, intervalMs);
}
